import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { tourEndpoints } from 'services/tourEndpoints';
import { driverEndpoints } from 'services/driverEndpoints';
import { waypointEndpoints } from 'services/waypointEndpoints';
import { deliveryEndpoints } from 'services/deliveryEndpoints';
import { carrierEndpoints } from 'services/carrierEndpoints';
import { instructionEndpoints } from 'services/instructionEndpoints';
import { DestinationJson } from 'app/models/Destination';
import { ColorJSON } from 'app/models/Color';
import { partOrderEndpoints } from 'services/partOrderEndpoints';
import { vehicleEndpoints } from 'services/vehicleEndpoints';
import { trackingEndpoints } from 'services/trackingEndpoints';
import baseQueryWithErrorHandling from 'services/kewloxBaseQuery';
import { orderEndpoints } from 'services/orderEndpoints';
import { pickingOrderEndpoints } from 'services/pickingOrderEndpoints';

interface PickupQueryInfos {
  orderId: string;
  pickupCode: string;
}

interface PickupQueryInfosResponse {
  orderId: string;
  status: number;
}

interface UserInfo {
  id: number;
  firstName: string;
  lastName: string;
  kewnetEmployeeId: number;
  groups: string[];
  pin: boolean; // Indicates if PIN protection is required for this user
}

export const kewloxApi = createApi({
  reducerPath: 'kewloxApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: [
    'Waypoint',
    'Delivery',
    'Driver',
    'CarrierConfiguration',
    'Tour',
    'PartOrder',
    'Carrier',
    'Destination',
    'Color',
    'Vehicle',
    'VehicleLocation',
    'GeofenceEvent',
    'User',
  ],
  endpoints: builder => ({
    login: builder.mutation({
      query: credentials => ({
        url: '/auth/token/',
        method: 'POST',
        body: credentials,
      }),
    }),
    validatePin: builder.mutation<
      { valid: boolean; message: string; attemptsRemaining?: number },
      { pin: string }
    >({
      query: ({ pin }) => ({
        url: '/user/pin_validation/',
        method: 'POST',
        body: { pin },
      }),
    }),
    passwordResetRequest: builder.mutation({
      query: (data: { identifier: string }) => ({
        url: '/user/password_reset_request/',
        method: 'POST',
        body: data,
      }),
    }),
    passwordResetConfirm: builder.mutation({
      query: (data: { token: string; uid: string; new_password: string }) => ({
        url: '/user/password_reset_confirm/',
        method: 'POST',
        body: data,
      }),
    }),
    getCurrentUser: builder.query<UserInfo, void>({
      query: () => '/user/me/',
      providesTags: ['User'],
    }),
    getOrderPickupInfo: builder.query<
      PickupQueryInfosResponse,
      PickupQueryInfos
    >({
      query: ({ orderId, pickupCode }) => ({
        url: `order/${orderId}/pickup_infos/?pc=${pickupCode}`,
        method: `GET`,
      }),
    }),
    getDestinations: builder.query<DestinationJson[], void>({
      query: () => `/destination/`,
      providesTags: ['Destination'],
    }),
    getColors: builder.query<ColorJSON[], void>({
      query: () => `/color/`,
      providesTags: ['Color'],
    }),
    ...carrierEndpoints(builder),
    ...waypointEndpoints(builder),
    ...driverEndpoints(builder),
    ...tourEndpoints(builder),
    ...deliveryEndpoints(builder),
    ...vehicleEndpoints(builder),
    ...trackingEndpoints(builder),
    ...partOrderEndpoints(builder),
    ...orderEndpoints(builder),
    ...instructionEndpoints(builder),
    ...pickingOrderEndpoints(builder),
  }),
});

export const {
  useLoginMutation,
  useValidatePinMutation,
  usePasswordResetRequestMutation,
  usePasswordResetConfirmMutation,
  useGetAllWaypointsQuery,
  useGetWaypointQuery,
  useAddWaypointMutation,
  useUpdateWaypointMutation,
  useDeleteWaypointMutation,
  useGetOrderPickupInfoQuery,
  useGetCarrierConfigurationQuery,
  useUpdateCarrierConfigurationMutation,
  useGetAllDriversQuery,
  useGetDriverQuery,
  useAddDriverMutation,
  useUpdateDriverMutation,
  useDeleteDriverMutation,
  useGetTourHistoryQuery,
  useNotifyAllCustomersMutation,
  useNotifyDeliveryCustomerMutation,
  useSendDeliveryConfirmationReminderMutation,
  useSendTourConfirmationRemindersMutation,
  useGetActiveToursQuery,
  useGetTourQuery,
  useAddDeliveryMutation,
  useGetDeliveryQuery,
  useUpdateDeliveryMutation,
  useDeleteDeliveryMutation,
  useConfirmDeliveryMutation,
  useCarrierReservationMutation,
  useActionDeliveryMutation,
  useGetCarriersQuery,
  useGetDestinationsQuery,
  useGetColorsQuery,
  useGetPartOrderQuery,
  useGetPartOrderByIdQuery,
  useUpdatePartOrderMutation,
  useSetDriversTourMutation,
  useResetPartOrderMutation,
  useSubdividePartOrderMutation,
  useMergePackagesMutation,
  useSplitPackagesMutation,
  useAddPackagesMutation,
  useUpdateTourMutation,
  useGetAllVehiclesQuery,
  useGetDeliveriesForToursQuery,
  useGetVehicleLocationsQuery,
  useGetGeofenceEventsQuery,
  useGetCurrentUserQuery,
  useGetInstructionsIndexQuery,
  useReactivateOrderMutation,
  useGetPickingOrderSettingsQuery,
} = kewloxApi;
